import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  accountsApi,
  clientAddressesApi,
  listStmtDeliveryMethodLookups,
  type AccountDto,
  type UpdateAccountRequest,
} from "@/lib/clients-accounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DetailModal } from "@/components/detail-modal";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Edit-account dialog
// ---------------------------------------------------------------------------

const CREDIT_PERIOD_OPTIONS = [0, 7, 14, 30, 60, 90];

export function EditAccountDialog({
  open,
  onOpenChange,
  account,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  account: AccountDto;
}) {
  const qc = useQueryClient();
  const [creditPeriodDays, setCreditPeriodDays] = useState<string>(
    account.creditPeriodDays != null ? String(account.creditPeriodDays) : ""
  );
  const [stmtDeliveryMethod, setStmtDeliveryMethod] = useState(account.stmtDeliveryMethod ?? "");
  const [emailAddress, setEmailAddress] = useState(account.emailAddress ?? "");
  const [ccEmailAddress, setCcEmailAddress] = useState(account.ccEmailAddress ?? "");
  const [accountAddressId, setAccountAddressId] = useState<string>(
    account.accountAddressId != null ? String(account.accountAddressId) : ""
  );

  const { data: deliveryMethods } = useQuery({
    queryKey: ["stmt-delivery-methods"],
    queryFn: listStmtDeliveryMethodLookups,
    staleTime: 60_000,
    enabled: open,
  });

  const { data: addresses } = useQuery({
    queryKey: ["client-addresses-picker", account.clientId],
    queryFn: () => clientAddressesApi.list(account.clientId!),
    enabled: open && account.clientId != null,
  });

  const activeAddresses = (addresses ?? []).filter((a) => !a.deleted);

  const editMut = useMutation({
    mutationFn: (body: UpdateAccountRequest) => accountsApi.patch(account.accountId, body),
    onSuccess: () => {
      toast.success("Account updated");
      void qc.invalidateQueries({ queryKey: ["account", account.accountId] });
      onOpenChange(false);
    },
    onError: () => toast.error("Failed to update account"),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    editMut.mutate({
      accountAddressId: accountAddressId ? Number(accountAddressId) : null,
      creditPeriodDays: creditPeriodDays ? Number(creditPeriodDays) : null,
      stmtDeliveryMethod: stmtDeliveryMethod || null,
      emailAddress: emailAddress || null,
      ccEmailAddress: ccEmailAddress || null,
      openingBalance: null,
      myobBalanceOutstanding: null,
    });
  }

  return (
    <DetailModal open={open} onOpenChange={onOpenChange} title="Edit account">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="ea-credit-period">Credit period</Label>
          <Select value={creditPeriodDays} onValueChange={setCreditPeriodDays}>
            <SelectTrigger id="ea-credit-period">
              <SelectValue placeholder="Select credit period" />
            </SelectTrigger>
            <SelectContent>
              {CREDIT_PERIOD_OPTIONS.map((d) => (
                <SelectItem key={d} value={String(d)}>{d} days</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="ea-delivery-method">Statement delivery method</Label>
          <Select value={stmtDeliveryMethod} onValueChange={setStmtDeliveryMethod}>
            <SelectTrigger id="ea-delivery-method">
              <SelectValue placeholder="Select delivery method" />
            </SelectTrigger>
            <SelectContent>
              {(deliveryMethods ?? []).filter((m) => !m.deleted).map((m) => (
                <SelectItem key={m.stmtDeliveryMethodCode} value={m.stmtDeliveryMethodCode}>
                  {m.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="ea-address">Account address</Label>
          <Select value={accountAddressId} onValueChange={setAccountAddressId} disabled={!account.clientId}>
            <SelectTrigger id="ea-address">
              <SelectValue placeholder="Select address" />
            </SelectTrigger>
            <SelectContent>
              {activeAddresses.map((a) => (
                <SelectItem key={a.id} value={String(a.id)}>
                  {a.formattedAddress ?? a.address}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="ea-email">Email</Label>
          <Input id="ea-email" type="email" value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="ea-cc-email">CC email</Label>
          <Input id="ea-cc-email" type="email" value={ccEmailAddress} onChange={(e) => setCcEmailAddress(e.target.value)} />
        </div>
        <Button type="submit" disabled={editMut.isPending}>
          {editMut.isPending ? "Saving…" : "Save"}
        </Button>
      </form>
    </DetailModal>
  );
}
